import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { createSelector } from 'reselect';

import { mergeDocumentsAsArray } from '../api/firebase/firestore';
import { createSearchConditions, fetchCategories, fetchTransactions } from './transactionsAPI';
import { transactionCategoriesFetchedAction, transactionsFetchedAction } from './transactionsRedux';
import { hasTransactionFilters, parseTransactionFilters } from './transactionsUrlFilters';
import { TransactionType } from './types';

const ROW_LIMIT = 10;

const selectTransactionCategories = createSelector(
  [(state) => state.transactions],
  (transactions) => transactions?.categories
);
const selectStatsList = createSelector([(state) => state.stats], (stats) => stats?.list ?? []);
const selectLocations = createSelector([(state) => state.locations], (locations) => locations ?? {});

/** Today's date in `timezone`, as ISO, so a bare `from` closes at the provider's today. */
const todayIsoIn = (timezone) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return parts; // en-CA yields YYYY-MM-DD
};

/**
 * Filters a deep link asks for, and whether they can be applied yet.
 *
 * Ownership sits here rather than in the search bar because the query is issued from the state
 * the list owns. Sending initial state upward from a child mount effect meant the first,
 * unfiltered request had already started, and the filtered one arrived too late to replace it.
 *
 * `ready` is false only while a date filter is waiting on the organization timezone. Day
 * boundaries differ by timezone, so querying before it resolves would filter the wrong window
 * and never correct itself. A link with no dates does not need the timezone and is ready at once.
 */
export function useInitialTransactionFilters() {
  const [searchParams] = useSearchParams();
  const { list, isLoaded } = useSelector(selectLocations);

  // The URL is read once. Later edits come from the controls, and re-reading would fight the
  // user by snapping the view back to whatever the link asked for.
  const [initialSearchParams] = useState(() => searchParams);

  const wantsDateFilter = initialSearchParams?.has?.('from') ?? false;
  const timezone = list?.[0]?.timezone ?? 'utc';
  const ready = !wantsDateFilter || isLoaded === true;

  return useMemo(() => {
    if (!ready) return { ready: false, initialFilters: null, initialSearchConditions: null };

    const initialFilters = parseTransactionFilters(initialSearchParams, todayIsoIn(timezone));
    const hasFilters = hasTransactionFilters(initialFilters);

    return {
      ready: true,
      initialFilters,
      initialSearchConditions: hasFilters
        ? createSearchConditions(
            initialFilters.searchField ?? '',
            initialFilters.searchText ?? '',
            initialFilters.transactionType ?? TransactionType.ANY,
            timezone
          )
        : null,
    };
  }, [ready, timezone, initialSearchParams]);
}

/**
 * @param {boolean} [enabled] false while the caller is still working out which conditions to
 *   use, so the first request is the right one rather than an unfiltered one it has to undo.
 */
export function useTransactions(organizationId, searchConditions, startAfter, extraFetchProps, enabled = true) {
  const dispatch = useDispatch();

  const [transactions, setTransactions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Identifies the most recent request. Previously this effect skipped entirely while another
  // fetch was in flight (`if (!loading)`), and because `loading` was not a dependency it never
  // retried — so conditions arriving mid-flight were dropped and the table kept showing the
  // unfiltered ledger. Every request now runs; only the newest is allowed to write state, so a
  // slow earlier response cannot overwrite a newer one.
  const latestRequest = useRef(0);

  const limit = ROW_LIMIT;

  useEffect(() => {
    if (!enabled) return;

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    const isStale = () => requestId !== latestRequest.current;

    const doFetch = async () => {
      try {
        setLoading(true);
        let _transactions = await fetchTransactions(organizationId, searchConditions, limit, startAfter);
        if (isStale()) return;

        // if this was a paginated fetch, we need to merge the new transactions with the existing ones
        if (startAfter && transactions?.lastDocument === startAfter) {
          _transactions = mergeDocumentsAsArray(transactions, _transactions);
        }
        setTransactions(_transactions);

        dispatch(transactionsFetchedAction(_transactions));
      } catch (error) {
        console.log(error);
        if (!isStale()) setError(error);
      } finally {
        if (!isStale()) setLoading(false);
      }
    };
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchConditions, organizationId, startAfter, extraFetchProps, enabled]);

  return { loading, error, transactions };
}

export function useCategories(organizationId) {
  const categoriesFromStore = useSelector(selectTransactionCategories);
  const dispatch = useDispatch();
  const [categories, setCategories] = useState(categoriesFromStore?.list);

  useEffect(() => {
    const doFetch = async () => {
      try {
        const _categories = await fetchCategories(organizationId);
        setCategories(_categories);
        dispatch(transactionCategoriesFetchedAction(_categories));
      } catch (error) {
        console.log(error);
      }
    };
    if (!categories) {
      if (categoriesFromStore) setCategories(categoriesFromStore);
      else doFetch();
    }
  }, [categories, categoriesFromStore, organizationId, dispatch]);
  return categories;
}

export function useTotalTransactionsCount() {
  const stats = useSelector(selectStatsList);
  return stats?.find((item) => item.id === 'transactions')?.total;
}
